import { Injectable } from '@angular/core';

const TERMS_ACCEPTANCE_STORAGE_KEY = 'acceptedTermsUsers';

@Injectable({ providedIn: 'root' })
export class TermsAcceptanceService {
  private readonly inMemoryStorage: string[] = [];

  hasAcceptedCurrentVersion(userId: string): boolean {
    if (!userId) {
      return false;
    }

    const acceptedUsers = this.readStorage();
    return acceptedUsers.includes(userId);
  }

  acceptCurrentVersion(userId: string): void {
    if (!userId) {
      return;
    }

    const acceptedUsers = [...this.readStorage()];

    if (!acceptedUsers.includes(userId)) {
      acceptedUsers.push(userId);
      this.writeStorage(acceptedUsers);
    }
  }

  clearAcceptance(userId: string): void {
    if (!userId) {
      return;
    }

    const acceptedUsers = this.readStorage().filter(id => id !== userId);
    this.writeStorage(acceptedUsers);
  }

  normalizeRedirectTo(redirectTo: string | null | undefined): string {
    if (!redirectTo) {
      return '/';
    }

    const trimmedRedirectTo = redirectTo.trim();
    if (!trimmedRedirectTo) {
      return '/';
    }

    const externalRedirect = this.normalizeExternalRedirect(trimmedRedirectTo);
    if (externalRedirect) {
      return externalRedirect;
    }

    return trimmedRedirectTo.startsWith('/') ? trimmedRedirectTo : '/';
  }

  private normalizeExternalRedirect(redirectTo: string): string | null {
    const candidate = /^www\./i.test(redirectTo) ? `https://${redirectTo}` : redirectTo;

    try {
      const parsed = new URL(candidate);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return null;
      }

      if (parsed.origin === globalThis.location.origin) {
        const normalized = parsed.pathname + parsed.search + parsed.hash;
        return normalized.startsWith('/') ? normalized : '/';
      }

      return parsed.toString();
    } catch {
      return null;
    }
  }

  private readStorage(): string[] {
    let raw: string | null;
    try {
      raw = localStorage.getItem(TERMS_ACCEPTANCE_STORAGE_KEY);
    } catch {
      return [...this.inMemoryStorage];
    }

    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
      }

      // Backward compatibility for previous object payload shape.
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'acceptedUsers' in parsed &&
        Array.isArray((parsed as { acceptedUsers?: unknown }).acceptedUsers)
      ) {
        return (parsed as { acceptedUsers: unknown[] }).acceptedUsers.filter(
          (id): id is string => typeof id === 'string' && id.trim().length > 0
        );
      }

      return [];
    } catch {
      return [];
    }
  }

  private writeStorage(userIds: string[]): void {
    const payload = [...new Set(userIds)];

    try {
      localStorage.setItem(TERMS_ACCEPTANCE_STORAGE_KEY, JSON.stringify(payload));
      this.inMemoryStorage.splice(0, this.inMemoryStorage.length, ...payload);
    } catch {
      // Fall back to in-memory state when storage is blocked or quota is exceeded.
      this.inMemoryStorage.splice(0, this.inMemoryStorage.length, ...payload);
    }
  }
}
